// University of Tennessee, Knoxville: public college page (#2652), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
// #3859 (2026-10-11), page vs KB: the cost table now follows the school's
// own 2026-27 cost of attendance page (cited as its own source), the figures
// the KB holds; the Common Data Set G1 budget it showed before differs.
//  - UT Knoxville's Common Data Set 2025-2026 (Institutional Research and
//    Strategic Analysis PDF): C1 applicants/admits/enrollees for Fall 2025
//    with the residency table, C8 test policy for Fall 2027, C21/C22 plans,
//    G1/G5 2026-2027 costs. Quirk: the C1 residency applicant columns sum to
//    53,840 while the total column (and the C1 counts by sex) read 53,841;
//    the rows below are as published.
//  - UT Admissions pages: First-Year Student Applications (dates, checklist,
//    program requirements), Important Dates and Deadlines, and the First Year
//    Test Score Policy. The deadlines pages carry no year label; the test
//    policy page dates the same January 20 materials deadline as January 20,
//    2027, so they are the Fall 2027 cycle.
//  - The CDS (C8E, C8F) lists January 15 for test scores and official scores
//    "prior to April"; the current admissions pages say January 20
//    and May 15, 2027. The admissions pages are used for dates.
//  - College Scorecard, school id 221759 (net price by family income). The
//    DEMO_KEY was over its rate limit, so the figures were read from the API
//    response the Scorecard's own school page loads (same API, same fields).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UT Knoxville Common Data Set 2025-2026',
        url: 'https://irsa.utk.edu/wp-content/uploads/sites/5/2026/07/CDS_2025-26_All__-1.pdf',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'UT Admissions, First-Year Student Applications',
        url: 'https://admissions.utk.edu/undergraduate-application/first-year-student/',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'UT Admissions, Important Dates and Deadlines',
        url: 'https://admissions.utk.edu/important-dates-and-deadlines/',
        read: '2026-10-01',
    },
    tests: {
        label: 'UT Admissions, First Year Test Score Policy',
        url: 'https://admissions.utk.edu/undergraduate-application/test-score-policy/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?221759-The-University-of-Tennessee-Knoxville',
        read: '2026-10-15',
    },
    coa: {
        label: 'UT One Stop, Cost of Attending UT - Undergraduate Student (2026-27)',
        url: 'https://onestop.utk.edu/billing-payments/cost-of-attending-ut-undergraduate-student/',
        read: '2026-10-11',
    },
};

const college = {
    slug: 'university-of-tennessee-knoxville',
    kbId: 'university_of_tennessee_knoxville',
    name: 'University of Tennessee, Knoxville',
    shortName: 'UT Knoxville',
    aliases: ['UTK', 'Tennessee'],
    location: 'Knoxville, TN',
    control: 'Public',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (UT brand standards); decoration only.
    branding: {
        primary: '#FF8200',
        secondary: '#4B4B4B',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_tennessee_knoxville_logo.png',
    },

    faq: {
        answer: { text: 'UT Knoxville admitted 35.1% of out-of-state first-year applicants for fall 2025, 14,526 of the 41,408 who applied, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1, residency table' },
        questions: [
            { q: 'What is the University of Tennessee acceptance rate?', a: 'For fall 2025, UT Knoxville admitted 23,464 of 53,841 first-year applicants, 43.6%. Tennessee residents were admitted at 72.8%, 8,725 of 11,980.', source: 'cds', where: 'section C1' },
            { q: 'When is the UT Knoxville early action deadline?', a: 'For fall 2027, submit your application by November 1 and your grades and test scores in STARS by November 15. Early Action decisions come out in early December for Tennessee residents and mid December for out-of-state applicants.', source: 'deadlines' },
            { q: 'What is the University of Tennessee early action acceptance rate?', a: 'UT Knoxville\'s Common Data Set lists a nonbinding early action plan but gives no early action counts, so there is no early action acceptance rate to report.', source: 'cds', where: 'sections C21 and C22' },
            { q: 'Are SAT or ACT scores required at UT Knoxville?', a: 'Yes. Every first-year applicant must self-report ACT or SAT scores by January 20, 2027, and only tests taken through December 2026 count. UT superscores, using your highest section scores.', source: 'tests' },
            { q: 'How much does UT Knoxville cost for out-of-state students?', a: 'For 2026-2027, out-of-state tuition is $31,672, and the total cost of attendance with on-campus housing is $57,448. For Tennessee residents the total is $36,994.', source: 'coa' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 53841,
        admitted: 23464,
        enrolled: 7143,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 11980, admitted: 8725 },
            { group: 'Out-of-state', applied: 41408, admitted: 14526 },
            { group: 'International', applied: 452, admitted: 213 },
        ],
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UT Knoxville\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UT Knoxville has no early decision. Its Common Data Set lists a nonbinding early action plan but gives no early action counts, so there is no early action acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to UT with a first-choice major. Some colleges ask for more:',
        rows: [
            { school: 'Tickle College of Engineering', requirement: 'Admission uses your core GPA and test scores: a Success Prediction Indicator of at least 60 and an ACT math score of 25 or SAT math of 590. Students below the test-score criteria get a holistic review, and the class may be limited by space.' },
            { school: 'College of Nursing', requirement: 'Test scores are required, and you must mark Nursing as your first-choice major to be considered for direct entry.' },
            { school: 'Natalie L. Haslam College of Music', requirement: 'Depending on the concentration you choose, you may need to audition, interview or both.' },
            { school: 'College of Architecture and Design', requirement: 'A portfolio of your artwork is recommended but not required. Faculty of the School of Architecture or School of Interior Architecture decide admission.' },
        ],
        note: 'Students who do not meet the engineering criteria can start as University Exploratory students and apply to the college again later.',
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 5' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'Early December for Tennessee residents, mid December for out-of-state applicants', binding: false },
            { plan: 'Regular Decision', deadline: 'January 5', decision: 'Early March', binding: false },
        ],
        note: 'These deadlines are for submitting the application. Your grades and test scores, entered in UT\'s STARS academic record, are due by the materials deadline: November 15 for Early Action and January 20 for Regular Decision. Enrollment confirmation is due May 1.',
        source: 'deadlines',
        binding: 'Neither plan is binding. Early action is nonbinding and not restrictive, so you can apply early to other schools too.',
        bindingSource: 'cds',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'UT Knoxville requires the ACT or SAT from every first-year applicant, a rule the University of Tennessee System set for all its campuses. Report your scores by January 20, 2027; only tests taken through December 2026 count. UT superscores, and the ACT Science section is not required for fall 2027. Official scores are due by May 15, 2027.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required to be considered for admission for fall 2027 applicants. It lists January 15 as the score date; the admissions page says January 20.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [11560, 31672] },
            { item: 'Fees', amounts: [2464, 2806] },
            { item: 'Housing (on campus)', amounts: [9572, 9572] },
            { item: 'Food', amounts: [5166, 5166] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1598, 1598] },
            { item: 'Transportation', amounts: [3500, 3500] },
            { item: 'Personal expenses', amounts: [3042, 3042] },
            { item: 'Loan fees', amounts: [92, 92] },
        ],
        totals: [36994, 57448],
        source: 'coa',
        netPrice: {
            average: 18314,
            byIncome: [
                { income: '$0 to $30,000', amount: 9626 },
                { income: '$30,001 to $48,000', amount: 11690 },
                { income: '$48,001 to $75,000', amount: 17499 },
                { income: '$75,001 to $110,000', amount: 22708 },
                { income: 'Over $110,000', amount: 26651 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UT\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
